import { assistantStore } from "@/store/assistant";
import { assistantSpaceFor } from "../use-assistant-space";

/** Persegi dalam koordinat viewport (sama dengan `PointerRect`, dipisah agar helper ini tak bergantung store). */
export interface AreaRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

/** Area konten yang terlihat: di bawah header, kanan sidebar/rel, kiri panel asisten (koordinat viewport). Header memendek di kanan saat panel terbuka, tetapi tepi bawahnya tetap. */
export interface ContentArea {
	left: number;
	top: number;
	right: number;
	bottom: number;
}

export interface CutSides {
	top: boolean;
	right: boolean;
	bottom: boolean;
	left: boolean;
}

export interface ClippedTarget {
	/** Bagian target yang berada di dalam area konten. */
	rect: AreaRect;
	/** Sisi yang terpotong area (bukan tepi asli elemen). */
	cut: CutSides;
	/** Jarak ring dari target per sisi: penuh bila muat, menyusut dekat tepi area, 0 di sisi terpotong. */
	pad: { top: number; right: number; bottom: number; left: number };
}

/** Jarak ring sorotan dari tepi target (px). */
export const RING_PAD = 6;

/** Celah antara tepi atas area (bawah header) dan target yang lebih tinggi dari area. */
export const SCROLL_TOP_GAP = 16;

const HEADER_SELECTOR = ".mantine-AppShell-header";
const NAVBAR_SELECTOR = ".mantine-AppShell-navbar";

export function contentAreaFor(input: {
	viewport: { width: number; height: number };
	/** Tepi bawah header tetap (0 bila halaman tanpa header, mis. /wall). */
	headerBottom: number;
	/** Tepi kanan sidebar/rel (0 bila tidak ada atau tersembunyi). */
	navRight: number;
	/** Lebar kolom panel asisten yang menutupi sisi kanan (0 = tertutup/diperbesar). */
	panelSpace: number;
}): ContentArea {
	return {
		left: Math.max(0, input.navRight),
		top: Math.max(0, input.headerBottom),
		right: Math.max(0, input.viewport.width - input.panelSpace),
		bottom: input.viewport.height,
	};
}

/** Baca geometri header, sidebar, dan panel dari DOM + store, lalu hitung area konten. */
export function readContentArea(doc: Document = document): ContentArea {
	const win = doc.defaultView ?? window;
	const header = doc.querySelector(HEADER_SELECTOR)?.getBoundingClientRect();
	const navbar = doc.querySelector(NAVBAR_SELECTOR)?.getBoundingClientRect();
	return contentAreaFor({
		viewport: { width: win.innerWidth, height: win.innerHeight },
		headerBottom: header && header.height > 0 ? header.bottom : 0,
		navRight: navbar && navbar.width > 0 ? navbar.right : 0,
		panelSpace: assistantSpaceFor(
			assistantStore.open,
			assistantStore.maximized,
		),
	});
}

/**
 * Potong target ke area konten. null bila target sama sekali di luar area.
 * `pad` = jarak ring yang diinginkan; menyusut agar ring tak menyentuh header/panel.
 */
export function clipToArea(
	rect: AreaRect,
	area: ContentArea,
	pad: number,
): ClippedTarget | null {
	const left = Math.max(rect.x, area.left);
	const top = Math.max(rect.y, area.top);
	const right = Math.min(rect.x + rect.width, area.right);
	const bottom = Math.min(rect.y + rect.height, area.bottom);
	if (right < left || bottom < top) return null;
	const cut: CutSides = {
		top: top > rect.y,
		right: right < rect.x + rect.width,
		bottom: bottom < rect.y + rect.height,
		left: left > rect.x,
	};
	const room = (cutSide: boolean, space: number) =>
		cutSide ? 0 : Math.max(0, Math.min(pad, space));
	return {
		rect: { x: left, y: top, width: right - left, height: bottom - top },
		cut,
		pad: {
			top: room(cut.top, top - area.top),
			right: room(cut.right, area.right - right),
			bottom: room(cut.bottom, area.bottom - bottom),
			left: room(cut.left, left - area.left),
		},
	};
}

/** Jepit titik ke dalam area konten (kursor tak boleh di atas header/panel). */
export function clampToArea(
	point: { x: number; y: number },
	area: ContentArea,
): { x: number; y: number } {
	return {
		x: Math.min(Math.max(point.x, area.left), Math.max(area.left, area.right)),
		y: Math.min(Math.max(point.y, area.top), Math.max(area.top, area.bottom)),
	};
}

export interface ScrollPlan {
	block: "center" | "start";
	/** `scroll-margin-top` sementara (px): menggeser titik acuan gulir ke bawah header. */
	marginTop: number;
}

/**
 * Rencana gulir: elemen yang muat di area konten ditaruh di tengah area; yang
 * lebih tinggi disejajarkan ke tepi atas area (di bawah header) + celah kecil.
 */
export function scrollPlanFor(
	rect: Pick<AreaRect, "height">,
	area: ContentArea,
	gap: number = SCROLL_TOP_GAP,
): ScrollPlan {
	const fits = rect.height + gap * 2 <= area.bottom - area.top;
	return fits
		? { block: "center", marginTop: area.top }
		: { block: "start", marginTop: area.top + gap };
}
